import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CircleAlert, KeyRound, Newspaper, Wallet } from 'lucide-react';
import { Banner, Card, EmptyState, Fact, Facts, PageHeader, StatusPill, TableSkeleton } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { ContentItemDrawer } from '@/components/content/ContentItemDrawer';
import { useRoles } from '@/hooks/useAuth';
import { useContentOverview } from '@/hooks/useContent';
import { CONTENT_MODE_LABELS, CONTENT_STATE, formatUsd } from '@/utils/content';
import { formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import type { ContentItemView, ContentOverview } from '@/types/api';

function ArticleRows({ items, overview, onOpen }: { readonly items: readonly ContentItemView[]; readonly overview: ContentOverview; readonly onOpen: (id: string) => void }): ReactNode {
  const names = new Map(overview.sites.map((site) => [site.siteId, site.name]));
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th scope="col">Статья</th>
            <th scope="col">Сайт</th>
            <th scope="col">Состояние</th>
            <th scope="col">Когда</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="row-link" onClick={() => onOpen(item.id)}>
              <td>
                <button type="button" className="link-button" onClick={() => onOpen(item.id)}>
                  {item.title}
                </button>
                {item.error ? <div className="field-error">{item.error}</div> : null}
              </td>
              <td className="muted">{names.get(item.siteId) ?? '—'}</td>
              <td>
                <StatusPill map={CONTENT_STATE} status={item.state} />
              </td>
              <td className="muted">{formatRelative(item.updatedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Every site's articles (ADR-0051): what waits for a person, what each site is set to, what it costs. */
export function ContentPage(): ReactNode {
  const roles = useRoles();
  const overview = useContentOverview();
  const [open, setOpen] = useState<string | null>(null);
  const data = overview.data;
  const spent = data !== undefined && data.budgetUsd !== null && data.monthUsd >= data.budgetUsd;

  return (
    <div className="page">
      <PageHeader title="Контент" subtitle="Статьи всех сайтов" />
      {overview.isPending ? (
        <Card flush>
          <TableSkeleton rows={4} columns={5} />
        </Card>
      ) : overview.isError ? (
        <ErrorState error={overview.error} />
      ) : data ? (
        <div className="stack">
          {!data.available ? (
            <Banner tone="warning" icon={KeyRound}>
              Ключ Claude не задан. {can(roles, 'secrets.manage') ? <Link to="/integrations/anthropic">Подключить</Link> : 'Его задаёт владелец или администратор.'}
            </Banner>
          ) : null}
          {spent ? (
            <Banner tone="warning" icon={Wallet}>
              Бюджет месяца исчерпан: статьи не пишутся до 1-го числа. Публикация готовых продолжается.
            </Banner>
          ) : null}

          <Card>
            <Facts>
              <Fact label="Расход за месяц">
                {formatUsd(data.monthUsd)}
                {data.budgetUsd !== null ? <span className="muted"> из {formatUsd(data.budgetUsd)}</span> : null}
              </Fact>
              <Fact label="Ждут проверки">{data.review.length}</Fact>
            </Facts>
          </Card>

          <Card flush title="Ждут проверки" icon={Newspaper}>
            {data.review.length === 0 ? <EmptyState icon={Newspaper} message="Готовых статей нет" /> : <ArticleRows items={data.review} overview={data} onOpen={setOpen} />}
          </Card>

          <Card flush title="Сайты">
            {data.sites.length === 0 ? (
              <EmptyState icon={Newspaper} message="Ни у одного сайта нет профиля контента. Он настраивается на вкладке «Контент» сайта" />
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Сайт</th>
                      <th scope="col">Режим</th>
                      <th scope="col">Автопилот</th>
                      <th scope="col" className="numeric">Готовы</th>
                      <th scope="col" className="numeric">В работе</th>
                      <th scope="col" className="numeric">В CMS</th>
                      <th scope="col">Последняя публикация</th>
                      <th scope="col" className="numeric">За месяц</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sites.map((site) => (
                      <tr key={site.siteId}>
                        <td>
                          <Link to={`/sites/${site.siteId}?tab=content`}>{site.name}</Link>
                          {site.domain ? <div className="muted mono">{site.domain}</div> : null}
                        </td>
                        <td>{site.mode ? CONTENT_MODE_LABELS[site.mode] : '—'}</td>
                        <td>{site.enabled ? `${site.perWeek} в неделю` : <span className="faint">выключен</span>}</td>
                        <td className="numeric">{site.counts.ready ?? 0}</td>
                        <td className="numeric">{(site.counts.queued ?? 0) + (site.counts.writing ?? 0) + (site.counts.publishing ?? 0)}</td>
                        <td className="numeric">{site.counts.published ?? 0}</td>
                        <td className="muted">{site.lastPublishedAt ? formatRelative(site.lastPublishedAt) : '—'}</td>
                        <td className="numeric">{formatUsd(site.monthUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {data.failed.length > 0 ? (
            <Card flush title="Ошибки" icon={CircleAlert}>
              <ArticleRows items={data.failed} overview={data} onOpen={setOpen} />
            </Card>
          ) : null}
        </div>
      ) : null}
      {open ? <ContentItemDrawer id={open} mayManage={can(roles, 'content.manage')} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
